import { format } from 'date-fns'
import React from 'react'

import { Card, LineItem, Tag } from '@/components'
import { RentalApplicationQueries } from '@/graphql/queries/rentalApplication.queries'
import reservationQueries from '@/graphql/queries/reservation.queries'
import { useGraphql } from '@/hooks/useGraphql'
import { RentalApplication } from '@/types/RentalApplication'
import { ReservationCheckinDetails, ReservationDetail } from '@/types/Reservation'
import { formatDate, formatPhoneDisplay, getJobStartTime, parseDate } from '@/util'

import { OccupantSection } from './OccupantSection'
import { ParkingSection } from './ParkingSection'
import { PetsSection } from './PetsSection'

export function MoveInView({ reservationData }: { reservationData: ReservationDetail }) {
  const rentalApplicationQuery = useGraphql<{ rentalApplication: RentalApplication }>(
    RentalApplicationQueries.QUERY_KEY,
    RentalApplicationQueries.getRentalApplication(),
    { id: reservationData.rentalApplicationId },
    {
      enabled: !!reservationData,
    },
  )

  const reservationCheckInQuery = useGraphql<{ reservation: ReservationCheckinDetails }>(
    'reservationCheckinDetails',
    reservationQueries.getReservationCheckinDetails,
    { id: reservationData.id },
    {
      enabled: !!reservationData,
    },
  )

  const ADMIN_URL = process.env.ADMIN_URL

  const rentalApplication = rentalApplicationQuery?.data?.rentalApplication
  const jobs = reservationCheckInQuery?.data?.reservation?.jobs || []

  const occupants = rentalApplication?.occupants || []

  const stayStart = reservationData.moveInDate
    ? formatDate(parseDate(reservationData.moveInDate), 'MM/dd/yyyy')
    : undefined
  const stayEnd = `${
    reservationData.moveOutDate ? formatDate(parseDate(reservationData.moveOutDate), 'MM/dd/yyyy') : 'Indefinite'
  }`

  const { professionalHousing, member, standby } = reservationData

  return (
    <>
      {rentalApplication && (
        <div className="mb-4 flex flex-wrap gap-3">
          <Tag className="mr-2 rounded-full text-sm text-semantic-info tag-blue-light">
            {occupants.length > 0 ? `Has Occupants` : `No Occupants`}
          </Tag>
          <Tag className="mr-2 rounded-full text-sm text-semantic-info tag-blue-light">
            {rentalApplication.needParking ? `Parking` : `No Parking`}
          </Tag>
          <Tag className=" rounded-full text-sm text-semantic-info tag-blue-light">
            {rentalApplication.hasPets ? `Has Pets` : `No Pets`}
          </Tag>
          {professionalHousing && (
            <Tag className=" mr-2 rounded-full text-sm  text-tag-green-dark tag-green-light">ProHo</Tag>
          )}
          {standby && <Tag className=" mr-2 rounded-full text-sm  text-tag-grey-dark tag-grey-light">Standby</Tag>}
          {member.organizationBlocksCommunication && (
            <Tag className=" mr-2 rounded-full text-sm text-tag-red-dark tag-red-light">No Communication</Tag>
          )}
        </div>
      )}
      {rentalApplication && (
        <div>
          <Card cardClassName="mb-4" headerText="Move In" minimizable={true}>
            <div className="flex w-full flex-col px-4">
              <LineItem label="Dates" value={`${stayStart} - ${stayEnd}`} />
              <LineItem
                label="Check in"
                tag={'success'}
                value={reservationData.moveInMethod == 'self' ? 'Self' : 'Host'}
              />
              <LineItem
                label="Check In Time"
                last={true}
                value={
                  reservationData.checkInAt ? format(new Date(reservationData.checkInAt + 'Z'), 'hh:mm a') : undefined
                }
              />
            </div>
          </Card>

          {reservationData.moveInMethod != 'self' && !reservationCheckInQuery?.isLoading && jobs.length > 0 && (
            <Card cardClassName="mb-4" headerText="Host Job" hideToStart={true} minimizable={true}>
              <div className="flex w-full flex-col px-4">
                <LineItem label="Name" value={jobs[0].host?.fullName || ''} />
                <LineItem label="Schedule Date" value={formatDate(parseDate(jobs[0]?.scheduleDate), 'MM/dd/yyyy')} />
                <LineItem label="Start time" value={getJobStartTime(jobs[0].startsAt, jobs[0]?.market?.timezone)} />
                <div className="flex w-full items-center justify-between py-2">
                  <div className="whitespace-nowrap py-2 text-sm leading-140">Admin Link</div>
                  <div className="rounded px-3 py-2 text-sm leading-140">
                    <a
                      className="text-action-primary underline"
                      href={`${ADMIN_URL}/admin/jobs/${jobs[0].id}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Link
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card cardClassName="mb-4" headerText="Member" hideToStart={true} minimizable={true}>
            <div className="flex w-full flex-col px-4">
              <LineItem label="Name" value={reservationData.member?.fullName || ''} />
              <LineItem label="Email" value={reservationData.member.email} />
              <LineItem label="DOB" value={reservationData.member.dateOfBirth} />
              <LineItem label="Phone" last={true} value={formatPhoneDisplay(reservationData.member.phone)} />
            </div>
          </Card>
          <ParkingSection rentalApplication={rentalApplication} />
          <PetsSection rentalApplication={rentalApplication} />
          <OccupantSection rentalApplication={rentalApplication} />
        </div>
      )}
    </>
  )
}
